/**
 * Zone coordinate math utilities — pure functions, zero dependencies on React/Zustand/Canvas.
 * All zone coordinates are normalized (0–1) relative to page dimensions.
 */

/** Normalized zone rectangle — all values 0–1 relative to page */
export type ZoneRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Canvas viewport state — pixel offsets and zoom level */
export type Viewport = {
  x: number;    // pixel offset of page origin within canvas
  y: number;
  scale: number; // zoom factor
};

/** Resize handle identifier for the 8 handles (4 corners + 4 edge midpoints) */
export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

/**
 * Normalize two corner points into a ZoneRect (x/y/width/height).
 * Handles drawing in any direction by taking min/max of the two corners.
 */
export function normalizeRect(x1: number, y1: number, x2: number, y2: number): ZoneRect {
  const x = Math.min(x1, x2);
  const y = Math.min(y1, y2);
  return {
    x,
    y,
    width: Math.max(x1, x2) - x,
    height: Math.max(y1, y2) - y,
  };
}

/**
 * Hit-test whether a normalized point (px, py) is inside the zone.
 * Includes the boundary edges.
 */
export function hitTestZone(px: number, py: number, zone: ZoneRect): boolean {
  return (
    px >= zone.x &&
    px <= zone.x + zone.width &&
    py >= zone.y &&
    py <= zone.y + zone.height
  );
}

/** Compute the 8 handle positions for a zone (normalized coords) */
function handlePositions(zone: ZoneRect): Record<HandleId, [number, number]> {
  const { x, y, width, height } = zone;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const r = x + width;
  const b = y + height;
  return {
    nw: [x,  y],
    n:  [cx, y],
    ne: [r,  y],
    e:  [r,  cy],
    se: [r,  b],
    s:  [cx, b],
    sw: [x,  b],
    w:  [x,  cy],
  };
}

/**
 * Check whether a normalized point is within handleRadius of any of the 8 resize handles.
 * Returns the HandleId of the closest matching handle, or null if none.
 */
export function hitTestHandle(
  px: number,
  py: number,
  zone: ZoneRect,
  handleRadius: number,
): HandleId | null {
  const positions = handlePositions(zone);
  const r2 = handleRadius * handleRadius;

  // Check corners first (higher priority than edge midpoints)
  const priority: HandleId[] = ['nw', 'ne', 'se', 'sw', 'n', 'e', 's', 'w'];

  for (const id of priority) {
    const [hx, hy] = positions[id];
    const dx = px - hx;
    const dy = py - hy;
    if (dx * dx + dy * dy <= r2) {
      return id;
    }
  }
  return null;
}

/**
 * Resize a zone by dragging a handle to the new normalized position (newX, newY).
 * Each handle moves different edges; dimensions are clamped to ≥ 0.
 */
export function resizeZone(zone: ZoneRect, handle: HandleId, newX: number, newY: number): ZoneRect {
  let { x, y, width, height } = zone;
  const right = x + width;
  const bottom = y + height;

  switch (handle) {
    case 'nw':
      x = newX;
      y = newY;
      width = right - newX;
      height = bottom - newY;
      break;
    case 'n':
      y = newY;
      height = bottom - newY;
      break;
    case 'ne':
      y = newY;
      width = newX - x;
      height = bottom - newY;
      break;
    case 'e':
      width = newX - x;
      break;
    case 'se':
      width = newX - x;
      height = newY - y;
      break;
    case 's':
      height = newY - y;
      break;
    case 'sw':
      x = newX;
      width = right - newX;
      height = newY - y;
      break;
    case 'w':
      x = newX;
      width = right - newX;
      break;
  }

  // Clamp to non-negative dimensions — adjust origin when width/height flips negative
  if (width < 0) {
    x += width;
    width = 0;
  }
  if (height < 0) {
    y += height;
    height = 0;
  }

  return { x, y, width, height };
}

/**
 * Convert canvas pixel coordinates to normalized page coordinates (0–1).
 * Formula: pagePixel = (canvasCoord - viewport.offset) / viewport.scale
 *          normalized = pagePixel / pageDimension
 */
export function canvasToPage(
  canvasX: number,
  canvasY: number,
  viewport: Viewport,
  pageWidth: number,
  pageHeight: number,
): [number, number] {
  const pagePixelX = (canvasX - viewport.x) / viewport.scale;
  const pagePixelY = (canvasY - viewport.y) / viewport.scale;
  return [pagePixelX / pageWidth, pagePixelY / pageHeight];
}

/**
 * Convert normalized page coordinates (0–1) to canvas pixel coordinates.
 * Inverse of canvasToPage.
 */
export function pageToCanvas(
  normalizedX: number,
  normalizedY: number,
  viewport: Viewport,
  pageWidth: number,
  pageHeight: number,
): [number, number] {
  const pagePixelX = normalizedX * pageWidth;
  const pagePixelY = normalizedY * pageHeight;
  return [
    pagePixelX * viewport.scale + viewport.x,
    pagePixelY * viewport.scale + viewport.y,
  ];
}

/**
 * R-125 (5.1) — Fit a page of (pageWidth × pageHeight) into the canvas
 * viewport (canvasWidth × canvasHeight) so the entire page is visible,
 * centered, with a small margin. Returns the viewport (scale + offset) that
 * achieves this. Used as the default zoom on first load and as the "Fit Page"
 * toolbar button.
 */
export function computeFitToPage(
  canvasWidth: number,
  canvasHeight: number,
  pageWidth: number,
  pageHeight: number,
  margin = 16,
): Viewport {
  if (pageWidth <= 0 || pageHeight <= 0 || canvasWidth <= 0 || canvasHeight <= 0) {
    return { x: 0, y: 0, scale: 1 };
  }
  const availW = Math.max(1, canvasWidth - margin * 2);
  const availH = Math.max(1, canvasHeight - margin * 2);
  const scale = Math.min(availW / pageWidth, availH / pageHeight);
  // Center the scaled page inside the canvas
  const scaledW = pageWidth * scale;
  const scaledH = pageHeight * scale;
  const x = (canvasWidth - scaledW) / 2;
  const y = (canvasHeight - scaledH) / 2;
  return { x, y, scale };
}

/**
 * R-125 (5.2) — Fit a page horizontally into the canvas so the page width
 * fills the canvas (with a margin). The vertical offset stays the same as
 * computeFitToPage (centered). When the scaled page is taller than the
 * canvas, the user pans vertically to reach the rest.
 */
export function computeFitToWidth(
  canvasWidth: number,
  canvasHeight: number,
  pageWidth: number,
  pageHeight: number,
  margin = 16,
): Viewport {
  if (pageWidth <= 0 || pageHeight <= 0 || canvasWidth <= 0 || canvasHeight <= 0) {
    return { x: 0, y: 0, scale: 1 };
  }
  const availW = Math.max(1, canvasWidth - margin * 2);
  const scale = availW / pageWidth;
  const scaledW = pageWidth * scale;
  const scaledH = pageHeight * scale;
  const x = (canvasWidth - scaledW) / 2;
  const y = Math.max(margin, (canvasHeight - scaledH) / 2);
  return { x, y, scale };
}

/**
 * R-125 (5.2) — Set the viewport to a specific zoom factor while keeping the
 * center of the canvas fixed (so the user sees their zoom expand from where
 * they were looking, not from the origin). Used by the % stepper buttons.
 */
export function applyZoomFactor(
  current: Viewport,
  newScale: number,
  canvasWidth: number,
  canvasHeight: number,
): Viewport {
  const cx = canvasWidth / 2;
  const cy = canvasHeight / 2;
  const ratio = newScale / current.scale;
  return {
    scale: newScale,
    x: cx - ratio * (cx - current.x),
    y: cy - ratio * (cy - current.y),
  };
}
